import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, Info, Minus } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatNumber } from '@blimo/shared';
import { Alert, AlertDescription, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, ErrorState, PageHeader, Skeleton } from '@blimo/ui';

const APP_LABELS = { admin: 'Verwaltung', portal: 'Kundenportal' };

/**
 * Administration › Rollen & Rechte (`roles.manage`): read-only Matrix Rollen × Rechte nach Bereichen.
 * Rollen sind im Code definiert (RolePermissionMatrix) – Bearbeiten ist bewusst nicht vorgesehen.
 */
export function RolesPage() {
  const api = useApi();
  const query = useQuery(adminQueries.roleMatrix(api));

  return (
    <>
      <PageHeader
        title="Rollen & Rechte"
        description="Welche Rolle welche Funktionen nutzen darf. Die Zuordnung von Rollen zu Benutzern erfolgt bei den Benutzern."
      />
      <Alert variant="info" className="mb-4">
        <Info aria-hidden="true" />
        <AlertDescription>
          Nur Ansicht: Rollen und ihre Rechte sind fest im System hinterlegt und werden mit Software-Updates gepflegt (nicht hier bearbeitbar). Welche
          Liegenschaften oder Einheiten jemand sieht, ergibt sich zusätzlich aus Eigentum, Mandaten und Objektrollen.
        </AlertDescription>
      </Alert>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          <RoleCards roles={query.data.roles} />
          <Matrix areas={query.data.areas} roles={query.data.roles} />
        </>
      )}
    </>
  );
}

function RoleCards({ roles }) {
  return (
    <ul className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Rollen">
      {roles.map((role) => (
        <li key={role.name}>
          <Card className="h-full">
            <CardHeader className="gap-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle as="h2" className="text-base">
                  {role.label}
                </CardTitle>
                {role.app && <Badge variant={role.app === 'admin' ? 'secondary' : 'outline'}>{APP_LABELS[role.app] ?? role.app}</Badge>}
              </div>
              {role.description && <CardDescription>{role.description}</CardDescription>}
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground">{role.all_permissions ? 'Alle Rechte' : `${formatNumber(role.permissions.length)} Rechte`}</span>
              <Link
                to="/administration/benutzer"
                search={{ role: role.name }}
                className="font-medium text-primary hover:underline"
                aria-label={`${formatNumber(role.users_count)} Benutzer mit Rolle ${role.label} anzeigen`}
              >
                {formatNumber(role.users_count)} Benutzer
              </Link>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}

function Matrix({ areas, roles }) {
  const granted = (role, permission) => role.all_permissions || role.permissions.includes(permission);
  return (
    <div className="overflow-x-auto rounded-md border bg-card">
      <table className="w-full min-w-[56rem] border-collapse text-sm">
        <caption className="sr-only">Rechte je Rolle</caption>
        <thead className="sticky top-0 bg-muted/60 text-xs">
          <tr>
            <th scope="col" className="sticky left-0 z-10 bg-muted/60 px-3 py-2 text-left font-medium">
              Recht
            </th>
            {roles.map((role) => (
              <th key={role.name} scope="col" className="px-2 py-2 text-center font-medium whitespace-nowrap">
                {role.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {areas.map((area) => (
            <Fragment key={area.key}>
              <tr className="border-t bg-muted/30">
                <th
                  scope="colgroup"
                  colSpan={roles.length + 1}
                  className="px-3 py-1.5 text-left text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                >
                  {area.label}
                </th>
              </tr>
              {area.permissions.map((permission) => (
                <tr key={permission.name} className="border-t hover:bg-muted/20">
                  <th scope="row" className="sticky left-0 z-10 bg-card px-3 py-2 text-left font-normal">
                    <span className="block font-medium">{permission.label}</span>
                    {permission.description && <span className="block text-xs text-muted-foreground">{permission.description}</span>}
                    <code className="text-[11px] text-muted-foreground">{permission.name}</code>
                  </th>
                  {roles.map((role) => (
                    <td key={role.name} className="px-2 py-2 text-center">
                      {granted(role, permission.name) ? (
                        <Check className="mx-auto size-4 text-success" aria-label={`${role.label}: ja`} />
                      ) : (
                        <Minus className="mx-auto size-4 text-muted-foreground/40" aria-label={`${role.label}: nein`} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
